<template>
  <div class="py-8">
    <div class="px-4 sm:px-6 lg:px-8">
      <!-- Dashboard Header -->
      <div class="mb-8 flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 class="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <span>Willkommen, {{ userInfo?.user?.fullName || userInfo?.user?.name }}</span>
            <span class="text-3xl">👋</span>
          </h1>
          <p class="text-gray-500 dark:text-gray-400">
            Ihre Übersicht im MBCOM ConnectCloud Portal.
          </p>
        </div>
        <div class="flex items-center gap-4">
          <div class="hidden md:block">
            <img
              alt="MBCOM Logo"
              class="h-20 w-auto"
              src="https://mbcom-public-assets.s3.eu-central-4.ionoscloud.com/MBCOM_logo_rgb.svg"
            />
          </div>
        </div>
      </div>

      <!-- Quick Access Section -->
      <div v-if="quickAccessLinks.length > 0" class="mb-8">
        <div
          class="flex flex-wrap items-center justify-between gap-8 p-8 bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-200 dark:border-gray-800"
        >
          <UTooltip v-for="link in quickAccessLinks" :key="link.name" :text="link.name">
            <div
              class="flex flex-col items-center gap-3 group cursor-pointer"
              @click="navigateTo(link.url)"
            >
              <div class="relative">
                <div
                  class="p-4 rounded-xl bg-gray-50 dark:bg-gray-800 text-gray-600 dark:text-gray-400 group-hover:bg-primary-50 dark:group-hover:bg-primary-900/50 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-all duration-200 shadow-sm group-hover:shadow-md group-hover:-translate-y-1"
                >
                  <UIcon :name="link.icon" class="w-8 h-8" />
                </div>
                <UBadge
                  v-if="link.environment"
                  :color="getEnvColor(link.environment)"
                  class="absolute -top-2 -right-2 px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-md shadow-sm"
                  size="xs"
                  variant="solid"
                >
                  {{ link.environment }}
                </UBadge>
              </div>
              <span
                class="text-xs font-semibold text-gray-500 dark:text-gray-400 group-hover:text-gray-900 dark:group-hover:text-white transition-colors"
              >
                {{ link.name }}
              </span>
            </div>
          </UTooltip>
        </div>
      </div>
      <!-- Product Dashboard Cards -->
      <div class="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-10">
        <!-- Workflow Product Card -->
        <UCard v-if="hasWorkflow" :class="{ 'lg:col-span-2': !hasInvoice && !hasMicrotechHub }">
          <template #header>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <UIcon class="w-6 h-6 text-primary-500" name="i-heroicons-cpu-chip" />
                <h3 class="text-xl font-bold text-gray-900 dark:text-white">Workflow</h3>
              </div>
              <UButton
                color="primary"
                icon="i-heroicons-arrow-right"
                label="Zur Übersicht"
                size="xs"
                to="/tasks"
                trailing
                variant="link"
              />
            </div>
          </template>

          <div class="space-y-8">
            <!-- Workflow Metrics -->
            <div class="grid grid-cols-1 sm:grid-cols-3 gap-4">
              <div
                class="p-3 bg-primary-50 dark:bg-primary-900/50 rounded-xl flex items-center gap-3 cursor-pointer hover:shadow-sm transition-shadow"
                @click="router.push('/tasks')"
              >
                <div
                  class="p-2 bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 rounded-lg shadow-sm"
                >
                  <UIcon class="w-5 h-5" name="i-heroicons-clipboard-document-list" />
                </div>
                <div>
                  <p class="text-xs text-primary-600 dark:text-primary-400 font-bold uppercase">
                    Offen
                  </p>
                  <p class="text-xl font-bold text-gray-900 dark:text-white">
                    {{ openTasksCount }}
                  </p>
                </div>
              </div>

              <div
                class="p-3 bg-blue-50 dark:bg-blue-900/50 rounded-xl flex items-center gap-3 cursor-pointer hover:shadow-sm transition-shadow"
                @click="router.push('/tasks')"
              >
                <div
                  class="p-2 bg-white dark:bg-gray-800 text-blue-600 dark:text-blue-400 rounded-lg shadow-sm"
                >
                  <UIcon class="w-5 h-5" name="i-heroicons-user" />
                </div>
                <div>
                  <p class="text-xs text-blue-600 dark:text-blue-400 font-bold uppercase">Eigene</p>
                  <p class="text-xl font-bold text-gray-900 dark:text-white">
                    {{ myTasksCount }}
                  </p>
                </div>
              </div>

              <div
                class="p-3 bg-orange-50 dark:bg-orange-900/50 rounded-xl flex items-center gap-3 cursor-pointer hover:shadow-sm transition-shadow"
                @click="router.push('/tasks')"
              >
                <div
                  class="p-2 bg-white dark:bg-gray-800 text-orange-600 dark:text-orange-400 rounded-lg shadow-sm"
                >
                  <UIcon class="w-5 h-5" name="i-heroicons-users" />
                </div>
                <div>
                  <p class="text-xs text-orange-600 dark:text-orange-400 font-bold uppercase">
                    Gruppe
                  </p>
                  <p class="text-xl font-bold text-gray-900 dark:text-white">
                    {{ groupTasksCount }}
                  </p>
                </div>
              </div>
            </div>

            <!-- Recent Workflow Tasks -->
            <div>
              <h4 class="font-bold text-sm text-gray-500 uppercase tracking-wider mb-4">
                Aktuelle Aufgaben
              </h4>
              <div v-if="tasks.length > 0" class="divide-y divide-gray-100 dark:divide-gray-800">
                <div
                  v-for="task in tasks.slice(0, 5)"
                  :key="task.guid"
                  class="py-3 flex items-center justify-between group cursor-pointer"
                  @click="router.push(`/tasks/${task.guid}`)"
                >
                  <div class="flex flex-col">
                    <span
                      class="font-medium text-gray-900 dark:text-white group-hover:text-primary-500 transition-colors"
                      >{{ task.objectNumber }}</span
                    >
                    <span class="text-xs text-gray-500">{{
                      task.currentStep?.name || 'In Bearbeitung'
                    }}</span>
                  </div>
                  <UIcon class="w-5 h-5 text-gray-300 group-hover:text-primary-500" name="i-heroicons-chevron-right" />
                </div>
              </div>
              <div v-else class="py-4 text-center text-gray-500 text-sm italic">
                Keine offenen Aufgaben.
              </div>
            </div>
          </div>
        </UCard>

        <!-- Invoice Product Card -->
        <UCard v-if="hasInvoice" :class="{ 'lg:col-span-2': !hasWorkflow && !hasMicrotechHub }">
          <template #header>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <UIcon class="w-6 h-6 text-primary-500" name="i-heroicons-document-text" />
                <h3 class="text-xl font-bold text-gray-900 dark:text-white">Invoice</h3>
              </div>
              <div class="flex gap-2">
                <UButton
                  color="primary"
                  icon="i-heroicons-chart-bar"
                  label="Statistik"
                  size="xs"
                  to="/admin/statistics"
                  variant="link"
                />
              </div>
            </div>
          </template>

          <div class="space-y-8">
            <!-- Invoice Metrics -->
            <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div
                class="p-3 bg-green-50 dark:bg-green-900/50 rounded-xl flex items-center gap-3 cursor-pointer hover:shadow-sm transition-shadow"
                @click="router.push('/admin/statistics')"
              >
                <div
                  class="p-2 bg-white dark:bg-gray-800 text-green-600 dark:text-green-400 rounded-lg shadow-sm"
                >
                  <UIcon class="w-5 h-5" name="i-heroicons-calendar" />
                </div>
                <div>
                  <p class="text-xs text-green-600 dark:text-green-400 font-bold uppercase">Dieser Monat</p>
                  <p class="text-xl font-bold text-gray-900 dark:text-white">
                    {{ recentDocumentsCount }}
                  </p>
                </div>
              </div>
            </div>

          </div>
        </UCard>
      </div>

        <!-- Microtech Hub Card -->
        <UCard
          v-if="hasMicrotechHub"
          class="shadow-sm border-primary-100 dark:border-primary-900/30"
          :class="{
            'lg:col-span-2': (hasWorkflow && hasInvoice) || (!hasWorkflow && !hasInvoice),
          }"
        >
          <template #header>
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <UIcon class="w-6 h-6 text-primary-500" name="i-heroicons-square-3-stack-3d" />
                <h3 class="text-xl font-bold text-gray-900 dark:text-white">microtech Daten</h3>
              </div>
              <p class="text-xs text-gray-500 hidden sm:block">ERP-Ressourcen</p>
            </div>
          </template>

          <div class="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <div
              v-for="link in microtechHubLinks"
              :key="link.name"
              class="p-3 bg-gray-50 dark:bg-gray-800/50 rounded-xl flex items-center gap-3 cursor-pointer hover:bg-primary-50 dark:hover:bg-primary-900/30 transition-all border border-gray-100 dark:border-gray-800 group"
              @click="navigateTo(link.url)"
            >
              <div
                class="p-2 bg-white dark:bg-gray-800 text-primary-600 dark:text-primary-400 rounded-lg shadow-sm group-hover:scale-110 transition-transform flex-shrink-0"
              >
                <UIcon :name="link.icon" class="w-5 h-5" />
              </div>
              <div class="min-w-0 flex-1">
                <p class="text-sm font-bold text-gray-900 dark:text-white truncate">
                  {{ link.name }}
                </p>
                <div class="flex items-center gap-1 text-[10px] text-primary-500 font-medium">
                  <span>Öffnen</span>
                  <UIcon name="i-heroicons-arrow-small-right" class="w-3 h-3" />
                </div>
              </div>
            </div>
          </div>
        </UCard>
      </div>
    </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { storeToRefs } from 'pinia'
import { PERMISSIONS, hasPermission } from '@/utils/roles'
import axios from 'axios'
import type { QuickLink, InstanceState, MonthlyStatisticView } from '@/types'

const router = useRouter()
const authStore = useAuthStore()
const { userInfo } = storeToRefs(authStore)

const tasks = ref<InstanceState[]>([])
const statistics = ref<MonthlyStatisticView[]>([])
const loadingDashboard = ref(false)

const DASHBOARD_CACHE_KEY = 'dashboard_data_cache'

const loadCache = () => {
  const cached = localStorage.getItem(DASHBOARD_CACHE_KEY)
  if (cached) {
    try {
      const data = JSON.parse(cached)
      if (data.tasks) tasks.value = data.tasks
      if (data.statistics) statistics.value = data.statistics
    } catch (e) {
      console.error('Failed to parse dashboard cache', e)
    }
  }
}

const saveCache = () => {
  localStorage.setItem(
    DASHBOARD_CACHE_KEY,
    JSON.stringify({
      tasks: tasks.value,
      statistics: statistics.value,
    })
  )
}

loadCache()

const openTasksCount = computed(() => tasks.value.length)
const myTasksCount = computed(() => {
  const user = userInfo.value?.user
  if (!user) return 0
  return tasks.value.filter((t) => {
    if (!t.claimedBy) return false
    return (
      t.claimedBy === user.email || t.claimedBy === user.fullName || t.claimedBy === user.name
    )
  }).length
})
const groupTasksCount = computed(() => tasks.value.length - myTasksCount.value)
const recentDocumentsCount = computed(() => {
  if (statistics.value.length === 0) return 0
  return statistics.value[0]?.totalSum || 0
})

const hasWorkflow = computed(() => {
  return (
    tasks.value.length > 0 ||
    userInfo.value?.quickLinks.some(
      (l) =>
        l.category === 'Workflow' ||
        l.url.includes('/tasks') ||
        l.name.toLowerCase().includes('workflow')
    ) ||
    false
  )
})

const hasInvoice = computed(() => {
  return (
    recentDocumentsCount.value > 0 ||
    userInfo.value?.quickLinks.some(
      (l) =>
        l.category === 'Invoice' ||
        l.url.includes('/statistics') ||
        l.name.toLowerCase().includes('invoice')
    ) ||
    false
  )
})

const hasMicrotechHub = computed(() => {
  return microtechHubLinks.value.length > 0
})

const microtechHubLinks = computed(() => {
  if (!hasPermission(userInfo.value?.user.permissions, PERMISSIONS.MICROTECH_DATA_READ)) {
    return []
  }

  const allQuickLinks = userInfo.value?.quickLinks || []

  const defaultTargets = [
    {
      name: 'Adressen',
      icon: 'i-heroicons-user-group',
      url: '/microtech-hub/addresses',
      keywords: ['address', 'adresse'],
    },
    {
      name: 'Artikel',
      icon: 'i-heroicons-cube',
      url: '/microtech-hub/products',
      keywords: ['product', 'artikel', 'produkte'],
    },
    {
      name: 'Projekte',
      icon: 'i-heroicons-folder',
      url: '/microtech-hub/projects',
      keywords: ['project', 'projekte'],
    },
    {
      name: 'Vorgänge',
      icon: 'i-heroicons-clipboard-document-list',
      url: '/microtech-hub/transactions',
      keywords: ['transaction', 'vorgang', 'vorgaenge'],
    },
  ]

  const result: QuickLink[] = []

  defaultTargets.forEach((target) => {
    const found = allQuickLinks.find((l) =>
      target.keywords.some(
        (k) => l.url.toLowerCase().includes(k) || l.name.toLowerCase().includes(k)
      )
    )

    if (found) {
      result.push(found)
    } else {
      result.push({
        name: target.name,
        icon: target.icon,
        url: target.url,
        category: 'microtech-hub',
        categoryOrderNumber: 0,
        orderNumber: 0,
      })
    }
  })

  // Add any other links that are explicitly categorized as Microtech Hub
  const otherHubLinks = allQuickLinks.filter(
    (l) =>
      l.category.toLowerCase() === 'microtech-hub' && !result.some((r) => r.url === l.url)
  )

  return [...result, ...otherHubLinks]
})

const fetchDashboardData = async () => {
  loadingDashboard.value = true
  try {
    const [tasksRes, statsRes] = await Promise.allSettled([
      axios.get('/api/workflow/v1/tasks'),
      axios.get('/api/registry/v1/statistics'),
    ])

    if (tasksRes.status === 'fulfilled') tasks.value = tasksRes.value.data || []
    if (statsRes.status === 'fulfilled') statistics.value = statsRes.value.data || []
    saveCache()
  } catch (error) {
    console.error('Failed to fetch dashboard data', error)
  } finally {
    loadingDashboard.value = false
  }
}

const quickAccessLinks = computed(() => {
  const links = userInfo.value?.quickLinks?.filter((link) => link.category === 'Quick') || []
  // Filter out links that are already shown in the Microtech Hub card
  const filtered = links.filter(
    (l) => !microtechHubLinks.value.some((m) => m.url === l.url)
  )
  return [...filtered].sort((a, b) => (a.orderNumber || 0) - (b.orderNumber || 0))
})

onMounted(async () => {
  if (!userInfo.value) {
    try {
      await authStore.fetchUserInfo()
    } catch (error) {
      console.error('Failed to fetch user info', error)
    }
  }
  fetchDashboardData()
})

const navigateTo = (url: string | undefined) => {
  if (!url) return
  if (url.startsWith('http')) {
    window.open(url, '_blank')
  } else if (url.startsWith('/')) {
    router.push(url)
  } else {
    window.location.href = url
  }
}

const getEnvColor = (env: string) => {
  switch (env) {
    case 'PROD':
      return 'error'
    case 'STAGING':
      return 'warning'
    case 'TEST':
      return 'success'
    case 'DEMO':
      return 'info'
    default:
      return 'neutral'
  }
}
</script>
